import './Sass/App.scss';
import React, {lazy, Suspense, useContext, useEffect, useState} from 'react'
import {BrowserRouter as Router, Redirect, Route, Switch, useLocation} from "react-router-dom";
import About from 'Pages/Landing/About'
import {BlogPageDisplay} from 'Pages/Landing/Blogs'
import {BlogPostDisplay} from 'Pages/Landing/BlogPost'
import Login from 'Pages/Landing/Login'
import Register, {EmailVerificationFailure, EmailVerificationSuccess} from 'Pages/Landing/Register'
import Privacy from 'Pages/Landing/Privacy'
import TermsOfService from 'Pages/Landing/TermsOfService'
import Contact from 'Pages/Landing/Contact'
import Recover from 'Pages/Landing/Recover'
import StudentLanding from 'Pages/Landing/CollegeLanding'
import CollegeLanding from 'Pages/Landing/CollegeLanding'
import {DiscoverPageDisplay} from './Pages/Discover/Discover'
import University from './Pages/Discover/University'
import Invites from './Pages/Dashboard/Invites'

import {getSessionCookie, SessionContext, setSessionCookie,} from './Utils/sessions';
import {createBrowserHistory} from "history";
import useDeepCompareEffect from 'use-deep-compare-effect'
import {Root} from 'Root';
import Spinner from 'react-bootstrap/Spinner'
import Search from './Pages/Discover/Search';
import Loading from "./Pages/Landing/Loading";
import MentorProfile from "./Pages/Dashboard/Profile/MentorProfile";
import UserProfile from "./Pages/Dashboard/Profile/UserProfile";
import EditUserProfile from "./Pages/Dashboard/Profile/EditUserProfile";

const history = createBrowserHistory();

const renderLoader = () => <div style={{
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'dodgerblue',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}}>
    <Spinner style={{color: "white"}} animation="border" role="status"></Spinner>
</div>;


const PublicRoute = ({component: ChildComponent, ...props}) => {
    const useQuery = () => {
        return new URLSearchParams(useLocation().search)
    }
    const data = useQuery().get('data')

    if (data) {
        localStorage.setItem('userInfo', data)
        const currentUrl = new URL(window.location.href);
        const params = currentUrl.searchParams;
        params.delete('data');
        const newUrl = currentUrl.pathname + currentUrl.search + currentUrl.hash; // Reconstruct the URL
        window.history.pushState(null, '', newUrl);
    }

    return <Suspense fallback={renderLoader()}>
        <ChildComponent {...props} />
    </Suspense>
}


const AuthenticationRoute = ({component: ChildComponent, ...props}) => {
    const {session} = useContext(SessionContext);
    const useQuery = () => {
        return new URLSearchParams(useLocation().search)
    }
    const data = useQuery().get('data')

    if (data) {
        localStorage.setItem('userInfo', data)
    }

    return (
        <Route
            {...props}
            render={(props) => {
                if (session.jwt) {
                    return <Redirect to='/dashboard'/>
                } else {
                    return <Suspense fallback={renderLoader()}>
                        <ChildComponent {...props} />
                    </Suspense>
                }
            }}
        />
    );
}

const ProtectedRoute = ({component: ChildComponent, ...props}) => {
    const {session} = useContext(SessionContext);
    const [user, setUser] = useState(undefined)
    const [loading, setLoading] = useState(false)
    const useQuery = () => {
        return new URLSearchParams(useLocation().search)
    }
    const singleAuthToken = useQuery().get('sat')
    const uid = useQuery().get('uid')
    const data = useQuery().get('data')

    if (data) {
        localStorage.setItem('userInfo', data)
    }

    useEffect(() => {
        let isMounted = true;

        if (!session.jwt && singleAuthToken) {
            Root.userApi.authenticateUserWithAuthToken(uid, singleAuthToken).then((res) => {
                if (isMounted) {
                    let cookie = res.data;
                    cookie.ambassadorData = undefined;
                    setUser(cookie)
                    setSessionCookie(cookie);
                    setLoading(false);
                }
            }).catch((err) => {
                if (isMounted) {
                    setLoading(false);
                }
            });
        }

        return () => {
            const currentUrl = new URL(window.location.href);
            const params = currentUrl.searchParams;
            params.delete('sat');
            params.delete('uid');
            const newUrl = currentUrl.pathname + currentUrl.search + currentUrl.hash; // Reconstruct the URL
            window.history.pushState(null, '', newUrl);
            isMounted = false;
        };
    }, [session, uid, singleAuthToken]);

    return (
        <Route
            {...props}
            render={(props) => {
                if (loading) {
                    return <Loading/>; // Show loading indicator while checking token/session
                }

                // After loading, check the session context for authentication
                if (user || session.jwt) {
                    // If authenticated, render the child component
                    // Suspense is kept in case ChildComponent uses React.lazy
                    return (
                        <Suspense fallback={renderLoader()}>
                            <ChildComponent {...props} />
                        </Suspense>
                    );
                } else {
                    // If not authenticated, redirect to the login page
                    // You might want to pass the intended destination via state
                    return (
                        <Redirect
                            to={{
                                pathname: '/login',
                                state: {from: props.location} // Pass original location
                            }}
                        />
                    );
                }
            }}
        />
    );
}

const PrivateRoute = ({component: ChildComponent, ...props}) => {
    const {session} = useContext(SessionContext);
    const useQuery = () => {
        return new URLSearchParams(useLocation().search)
    }
    const data = useQuery().get('data')

    if (data) {
        localStorage.setItem('userInfo', data)
    }
    return <Route {...props} render={() => {
        if (!session.jwt) {
            localStorage.setItem('prevLocation', window.location);
            return <Redirect to='/login'/>
        } else {
            return <Suspense fallback={renderLoader()}>
                <ChildComponent {...props} />
            </Suspense>
        }
    }}/>
}

function App() {
    const [session, setSession] = useState(getSessionCookie());
    const refreshSession = (newSession = undefined) => {
        if (session === undefined || session === {}) {

        } else if (newSession) {
            setSessionCookie(newSession)
            setSession(newSession)
        } else {
            Root.userApi.getSignedInUserInformation(session.jwt).then(res => {
                let user = res.data?.user;
                user.ambassadorData = undefined;
                user.jwt = res.data?.jwt;
                setSessionCookie(user);
                setSession(user);
            }).catch((err) => {
                console.log(err.response)
            })
        }
    };

    if (session.jwt) {
        Root.userApi.validateSessionToken(session.jwt)
            .catch(() => {
                setSessionCookie({})
                return <Redirect to='/login'/>
            })
    }

    const value = {session, refreshSession};
    useDeepCompareEffect(
        () => {
            setSession(getSessionCookie());
        },
        [session]
    );

// Lazy loading to improve performance
    const Schedule = lazy(() => import('./Pages/Dashboard/Schedule'));
    const Media = lazy(() => import('./Pages/Dashboard/Media'));
    const Settings = lazy(() => import('./Pages/Settings/Settings'));
    const Inbox = lazy(() => import('./Pages/Dashboard/Inbox'));
    const Meeting = lazy(() => import('./Pages/Dashboard/Meeting'));
    const Onboarding = lazy(() => import('./Pages/Discover/Onboarding'));
    const Dashboard = lazy(() => import('./Pages/Dashboard/Dashboard'));
    const StudentApplicantProfile = lazy(() => import('./Pages/Dashboard/Profile/StudentApplicantProfile'));
    const Payment = lazy(() => import('./Pages/Dashboard/Payment'));

    return (
        <SessionContext.Provider value={value}>
            <Router history={history}>
                <Switch>
                    <PublicRoute exact path="/" component={StudentLanding}/>
                    <PublicRoute path="/about" component={About}/>
                    <PublicRoute exact path="/recover" component={Recover}/>
                    <PublicRoute exact path="/email_verification_success" component={EmailVerificationSuccess}/>
                    <PublicRoute exact path="/email_verification_failed" component={EmailVerificationFailure}/>
                    <PublicRoute exact path="/privacy" component={Privacy}/>
                    <PublicRoute exact path="/tos" component={TermsOfService}/>
                    <PublicRoute exact path="/contact" component={Contact}/>
                    <PublicRoute exact path="/blog/posts/*" component={BlogPostDisplay}/>
                    <PublicRoute exact path="/blog" component={BlogPageDisplay}/>
                    <PublicRoute exact path="/blogs" component={BlogPageDisplay}/>
                    <PublicRoute exact path="/blog/*" component={BlogPageDisplay}/>
                    <PublicRoute exact path="/discover" component={DiscoverPageDisplay}/>
                    <PublicRoute exact path="/university" component={University}/>
                    <PublicRoute exact path="/search" component={Search}/>

                    <AuthenticationRoute exact path="/login" component={Login}/>
                    <AuthenticationRoute exact path="/register" component={Register}/>

                    <ProtectedRoute exact path="/onboarding" component={Onboarding}/>

                    <PrivateRoute exact path="/dashboard" component={Dashboard}/>
                    <PrivateRoute exact path="/students/:userName" component={StudentApplicantProfile}/>
                    <PrivateRoute exact path="/student/:userName" component={StudentApplicantProfile}/>
                    <PrivateRoute exact path="/mentors/:userName" component={MentorProfile}/>
                    <PrivateRoute exact path="/mentor/:userName" component={MentorProfile}/>
                    <PrivateRoute exact path="/profile" component={UserProfile}/>
                    <PrivateRoute exact path="/profile/edit" component={EditUserProfile}/>
                    <PrivateRoute exact path="/meeting" component={Meeting}/>
                    <PrivateRoute exact path="/inbox" component={Inbox}/>
                    <PrivateRoute exact path="/invites" component={Invites}/>
                    <PrivateRoute exact path="/payment" component={Payment}/>
                    <PrivateRoute exact path="/schedule" component={Schedule}/>
                    <PrivateRoute exact path="/media" component={Media}/>
                    <PrivateRoute exact path="/settings" component={Settings}/>

                    {/* page-not-found routes */}
                    <PrivateRoute exact path="/*" component={Dashboard}/>
                    <PublicRoute exact path={'/*'} component={CollegeLanding}/>
                </Switch>
            </Router>
        </SessionContext.Provider>
    );
}

export default App;
